/**
 * Masthead Search Modal
 */

import delegate from 'delegate'

import { on, trigger } from 'utils/events'
import * as tools from 'utils/tools'

const el = {
	container: document.querySelector('[data-js="masthead"]'),
	searchModal: document.querySelector('[data-js="search-modal"]'),
	searchModalWrapper: document.querySelector(
		'[data-js="search-modal-wrapper"]'
	),
}

/**
 * Update the ARIA attributes when closed
 *
 * @param triggerElements
 */
const closeAriaAttributes = (triggerElements) => {
	triggerElements.forEach((triggerEl) => {
		if (triggerEl.getAttribute('aria-expanded')) {
			triggerEl.setAttribute('aria-expanded', 'false')
		}
		if (triggerEl.getAttribute('data-label-hidden')) {
			triggerEl.setAttribute(
				'aria-label',
				triggerEl.getAttribute('data-label-hidden')
			)
		}
	})
}

/**
 * Update the ARIA attributes when opened
 *
 * @param triggerElements
 */
const openAriaAttributes = (triggerElements) => {
	triggerElements.forEach((triggerEl) => {
		if (triggerEl.getAttribute('aria-expanded')) {
			triggerEl.setAttribute('aria-expanded', 'true')
		}
		if (triggerEl.getAttribute('data-label-shown')) {
			triggerEl.setAttribute(
				'aria-label',
				triggerEl.getAttribute('data-label-shown')
			)
		}
	})
}

/**
 * Open search modal
 */
const openSearch = () => {
	// Go to the top of the page to better support CSS scroll/display lock.
	document.documentElement.scrollTop = document.body.scrollTop = 0

	// Trigger an event to inform other scripts of the change.
	trigger({ event: 'ur_core/search_active', native: false })

	el.container.classList.add('masthead--search-is-active')
	el.searchModalWrapper.classList.add('active')

	try {
		el.searchModal.showModal()
	} catch {
		// @TODO .masthead__search--open is not necessary when .showModal() is supported in all browsers.
		el.searchModal.classList.add('masthead__search--open')
	}

	document.documentElement.classList.add('no-scroll')

	openAriaAttributes(el.siteSearchTriggers)

	// Send focus to search input.
	// @TODO - Manual focus is not necessary when <dialog>.showModal() is supported in all browsers.
	el.searchModal.querySelector('input.search-form__query').focus()
}

/**
 * Close search modal
 */
const closeSearch = () => {
	try {
		el.searchModal.close()
	} catch {
		// @TODO .masthead__search--open is not necessary when .close() is supported in all browsers.
		el.searchModal.classList.remove('masthead__search--open')
	}

	el.container.classList.remove('masthead--search-is-active')
	el.searchModalWrapper.classList.remove('active')

	document.documentElement.classList.remove('no-scroll')

	closeAriaAttributes(el.siteSearchTriggers)
}

/**
 * Toggle the site search on mobile
 */
const toggleSearch = (event) => {
	event.preventDefault()
	event.stopPropagation()
	el.container.classList.contains('masthead--search-is-active')
		? closeSearch()
		: openSearch()
}

/**
 * Handle closing the search based on which is active.
 */
const handleOutActions = () => {
	if (el.container.classList.contains('masthead--search-is-active')) {
		closeSearch()

		// @TODO - Manual focus not necessary when <dialog>.close() is supported in all browsers.
		el.siteSearchTriggerTop.focus()
	}
}

/**
 * Handle clicking outside the masthead to close the site search for mobile and a submenu on desktop.
 *
 * @param e
 */
const handleClickOut = (e) => {
	if (!el.container.classList.contains('masthead--search-is-active')) {
		return
	}

	if (!tools.closest(e.target, '[data-js="search-modal"]')) {
		handleOutActions()
	}
}

/**
 * Handle the escape key to close the site search on mobile and submenu on desktop.
 *
 * @param e
 */
const handleEsc = (e) => {
	// Bail if the key press was not escape or the search is not active.
	if (
		e.which !== 27 ||
		!el.container.classList.contains('masthead--search-is-active')
	) {
		return
	}

	handleOutActions()
}

/**
 * Bind any even handlers this module utilizes.
 */
const bindEvents = () => {
	delegate(el.container, '[data-js^="toggle-search"]', 'click', toggleSearch)

	on(document, 'keyup', handleEsc)
	on(document, 'click', handleClickOut)
	on(document, 'ur_core/nav_active', closeSearch)
}

/**
 * Cache a number of elements we need for functionality.
 */
const cacheElements = () => {
	el.titleRow = el.container.querySelector('[data-js="title-row"]')
	el.siteNav = el.container.querySelector('[data-js="site-navigation"]')
	el.siteSearchTriggers = el.container.querySelectorAll(
		'[data-js^="toggle-search"]'
	)
	el.siteSearchTriggerTop = el.container.querySelector(
		'[data-js="toggle-search-top"]'
	)
}

/**
 * Init this module.
 */
const init = () => {
	if (!el.container || !el.searchModal) {
		return
	}

	cacheElements()
	bindEvents()
}

export default init
